import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslate } from '../../../components/TranslationText';
import { filterSemenOrders } from '../Api/semenMarketApi';
import { formatDate } from '../../../helper/formatDate';
import Loader from '../../../components/Loader';
import SearchBox from '../../../components/SearchBox';
import { OrderStatus } from '../constants/orderStatus';
import { OrderActions } from '../constants/semenActions';
import { formatDateTimeToString } from '../../../helper/formatDateTimeToString';

const PAGE_SIZE = 10;

/**
 * My Semen Requests — table with search and pagination.
 * Embedded inside OwnerProfile as a tab.
 */
export default function SemenRequestsPage({ toast }) {
    const { translate } = useTranslate();
    const [orders, setOrders] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    // ─── Fetch data ─────────────────────────────────────────
    const fetchOrders = useCallback(
        (page = 1, search = '') => {
            setLoading(true);
            const params = {
                skip: 0,
                take: 1000,
                ...(search && { search }),
            };
            filterSemenOrders(params)
                .then((res) => {
                    const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
                    setOrders(list);
                    setTotalCount(res?.totalCount ?? res?.total ?? list.length);
                })
                .catch(() => {
                    toast?.error?.(translate('web.somethingWentWrong', 'Something went wrong'));
                    setOrders([]);
                    setTotalCount(0);
                })
                .finally(() => setLoading(false));
        },
        [toast, translate]
    );

    useEffect(() => {
        fetchOrders(currentPage, searchTerm);
    }, [currentPage]);

    // ─── Search handler ─────────────────────────────────────
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearch = () => {
        setCurrentPage(1);
    };

    // ─── Client-side filter ─────────────────────────────────
    const filteredOrders = orders.filter((item) => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        return (
            (item.referenceNumber || item.refNo || '').toLowerCase().includes(term) ||
            (item.stallionName || item.horseName || '').toLowerCase().includes(term) ||
            (item.mareName || '').toLowerCase().includes(term) ||
            (item.status || '').toLowerCase().includes(term)
        );
    });

    // ─── Pagination ─────────────────────────────────────────
    const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
    const paginatedOrders = filteredOrders.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // ─── Render ─────────────────────────────────────────────
    return (
        <>
            {/* Search */}
            <div className="row rtl mb-main">
                <div className="col-sm-6 col-12">
                    <SearchBox
                        isFullWidth
                        isForm={false}
                        isWithSubmitBTN={true}
                        onSubmitBtnFunction={handleSearch}
                        onChange={handleSearchChange}
                        value={searchTerm}
                        placeholder={translate('web.searchByRefNo', 'Search By Ref.No')}
                    />
                </div>
            </div>

            {/* Loading */}
            {loading && <Loader />}

            {/* Table */}
            {!loading && (
                <div className="scrolableTable mb-main">
                    <table className="table table-striped table-bordered rtl horseResultTable">
                        <thead className="theadHorseSelect">
                            <tr>
                                <th className="text-center">
                                    {translate('web.refNo', 'Ref.No')}
                                </th>
                                <th className="text-center">
                                    {translate('web.stallionName', 'Stallion Name')}
                                </th>
                                <th className="text-center">
                                    {translate('web.mareName', 'Mare Name')}
                                </th>
                                <th className="text-center">
                                    {translate('web.createdDate', 'Created Date')}
                                </th>
                                <th className="text-center">
                                    {translate('web.status', 'Status')}
                                </th>
                                <th className="text-center">
                                    {translate('web.action', 'Action')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="text-center">
                            {paginatedOrders.length > 0 ? (
                                paginatedOrders.map((item, index) => {
                                    const canEdit = item.status === OrderStatus.Return;
                                    const isPendingPayment = item.status === OrderStatus.PendingPayment;
                                    const isCreateAdditionalDose = item.canCreateAdditionalDose;
                                    const statusKey = item.status
                                        ? `web.${item.status.toLowerCase()}`
                                        : '';
                                    return (
                                        <tr key={item.id || index}>
                                            <td style={{ fontSize: 13 }}>
                                                <div>{item.referenceNumber || item.refNo || '-'}</div>
                                                {item.isAdditionalDose && (
                                                    <div
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            background: 'linear-gradient(270deg, #6366f1 0%, #a855f7 100%)',
                                                            color: '#fff',
                                                            padding: '2px 10px',
                                                            borderRadius: '50px',
                                                            fontSize: '11px',
                                                            fontWeight: 'bold',
                                                            marginTop: '4px',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        }}
                                                    >
                                                        <i
                                                            className="fa-solid fa-circle-plus"
                                                            style={{ marginRight: '6px', fontSize: '14px' }}
                                                        ></i>
                                                        {translate('web.extraDose', 'ExtraDose')}
                                                    </div>
                                                )}
                                                {item.isLiveFoal && (
                                                    <div
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            background: 'linear-gradient(270deg, #10b981 0%, #34d399 100%)',
                                                            color: '#fff',
                                                            padding: '2px 10px',
                                                            borderRadius: '50px',
                                                            fontSize: '11px',
                                                            fontWeight: 'bold',
                                                            marginTop: '4px',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                            marginLeft: item.isAdditionalDose ? '4px' : '0'
                                                        }}
                                                    >
                                                        <i
                                                            className="fa-solid fa-file-pen"
                                                            style={{ marginRight: '6px', fontSize: '14px' }}
                                                        ></i>
                                                        {translate('web.liveFoal', 'Live Foal')}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ fontSize: 13 }}>
                                                {item.stallionName || item.horseName || '-'}
                                            </td>
                                            <td style={{ fontSize: 13 }}>
                                                {item.mareName || '-'}
                                            </td>
                                            <td style={{ fontSize: 13 }}>
                                                {item.createdDate ? formatDateTimeToString(item.createdDate) : '-'}
                                            </td>
                                            <td style={{ fontSize: 13 }}>
                                                {item.statusName
                                                    ? item.statusName
                                                    : '-'}
                                            </td>
                                            <td>
                                                <div className="d-flex justify-content-center gap-2">
                                                    {canEdit && (
                                                        <Link
                                                            to={`/semenCheckout?orderId=${item.id}&action=${OrderActions.Edit}`}
                                                            className="text-primary"
                                                            title={translate('web.edit', 'Edit')}
                                                        >
                                                            <i className="fa-solid fa-pen-to-square" />
                                                        </Link>
                                                    )}
                                                    {isPendingPayment && (
                                                        <Link
                                                            to={`/semenCheckout?orderId=${item.id}&action=${OrderActions.Payment}`}
                                                            className="text-success"
                                                            title={translate('web.completePayment', 'Complete Payment')}
                                                        >
                                                            <i className="fa-solid fa-pen-to-square" />
                                                        </Link>
                                                    )}
                                                    <Link
                                                        to={`/semenCheckout?orderId=${item.id}&action=${OrderActions.View}`}
                                                        style={{ color: 'var(--mainGold)' }}
                                                        title={translate('web.view', 'View')}
                                                    >
                                                        <i className="fa-solid fa-eye" />
                                                    </Link>
                                                    {isCreateAdditionalDose && (
                                                        <Link
                                                            to={`/semenCheckout?orderId=${item.id}&semenMarketId=${item.semenMarketId || ''}&action=${OrderActions.ExtraDose}`}
                                                            className="text-info"
                                                            title={translate('web.extraDose', 'Extra Dose')}
                                                        >
                                                            <i className="fa-solid fa-plus-circle" />
                                                        </Link>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="text-center py-4" style={{ color: '#888' }}>
                                        {translate('web.noRequests', 'No requests found')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-2 mb-main">
                    <button
                        className="btn btn-outline-danger btn-sm"
                        disabled={currentPage <= 1}
                        onClick={() => goToPage(currentPage - 1)}
                    >
                        {translate('web.previous', 'Previous')}
                    </button>
                    <span
                        className="btn btn-danger btn-sm"
                        style={{ minWidth: 40, cursor: 'default' }}
                    >
                        {currentPage}/{totalPages}
                    </span>
                    <button
                        className="btn btn-outline-danger btn-sm"
                        disabled={currentPage >= totalPages}
                        onClick={() => goToPage(currentPage + 1)}
                    >
                        {translate('web.next', 'Next')}
                    </button>
                </div>
            )}
        </>
    );
}
